import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  CancelPlayerWagerLimitMutation,
  CancelPlayerWagerLimitMutationVariables,
} from './__generated__/PlayerCancelWagerLimitForm';

const query = graphql`
  query SanityPlayerCancelWagerLimitForm {
    sanityPlayerCancelWagerLimitsForm {
      title {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const cancelPendingWagerLimitMutation = gql`
  mutation CancelPlayerWagerLimit(
    $playerId: ID!
    $limitPeriodType: LimitTypeEnum!
  ) {
    cancelPendingWagerLimit(
      playerId: $playerId
      limitPeriodType: $limitPeriodType
    ) {
      id
      wagerLimit {
        dayLimit {
          ...PeriodLimit
        }
        weekLimit {
          ...PeriodLimit
        }
        monthLimit {
          ...PeriodLimit
        }
      }
    }
  }
  ${limitFragment}
`;

const PlayerCancelWagerLimitForm: FC<{
  playerId: string;
  limitPeriodType: LimitTypeEnum;
}> = ({ playerId, limitPeriodType }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerCancelWagerLimitFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerCancelWagerLimitsForm;

  const [playerCancelWagerLimitState, cancelPendingWagerLimit] = useMutation<
    CancelPlayerWagerLimitMutation,
    CancelPlayerWagerLimitMutationVariables
  >(cancelPendingWagerLimitMutation);

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (_values: {}) => {
    if (playerId) {
      cancelPendingWagerLimit({
        playerId,
        limitPeriodType,
      }).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error?.message);
        } else if (close) {
          close();
        }
      });
    }
  };

  if (!form) {
    return null;
  }
  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={{}}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={playerCancelWagerLimitState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerCancelWagerLimitForm;
